import { useState, useEffect } from "react";
import axios from "axios";
import LoadingPage from "../components/Loader";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { showBackendNotification } from "../components/NotificationManager";
import { 
  FaBell, 
  FaCheckCircle, 
  FaExclamationTriangle,
  FaClock,
  FaEye,
  FaShoppingBag,
  FaReceipt,
  FaHistory,
  FaChevronRight,
  FaCircle
} from "react-icons/fa";

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [visibleNotifications, setVisibleNotifications] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    const token = localStorage.getItem("token");

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`https://mongobyte-hc73.onrender.com/api/v1/users/notifications`, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      const newNotifications = response.data.notifications;
      setNotifications(newNotifications.reverse()); 
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setError("Failed to load notifications. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const getNotificationIcon = (message) => {
    const lowerMessage = message.toLowerCase();
    if (lowerMessage.includes('order') || lowerMessage.includes('delivered')) 
      return <FaCheckCircle className="text-pepperoni" />;
    if (lowerMessage.includes('payment') || lowerMessage.includes('paid')) 
      return <FaReceipt className="text-cheese" />;
    if (lowerMessage.includes('delivery') || lowerMessage.includes('driver')) 
      return <FaShoppingBag className="text-pepperoni" />;
    if (lowerMessage.includes('transfer') || lowerMessage.includes('wallet')) 
      return <FaHistory className="text-cheese" />;
    if (lowerMessage.includes('login') || lowerMessage.includes('security')) 
      return <FaExclamationTriangle className="text-pepperoni" />;
    return <FaBell className="text-crust" />;
  };

  const getNotificationBorder = (message) => {
    const lowerMessage = message.toLowerCase();
    if (lowerMessage.includes('order') || lowerMessage.includes('delivered')) 
      return 'border-l-pepperoni bg-red-50/30';
    if (lowerMessage.includes('payment') || lowerMessage.includes('paid')) 
      return 'border-l-cheese bg-yellow-50/30';
    if (lowerMessage.includes('delivery') || lowerMessage.includes('driver')) 
      return 'border-l-pepperoni bg-red-50/30';
    if (lowerMessage.includes('transfer') || lowerMessage.includes('wallet')) 
      return 'border-l-cheese bg-yellow-50/30';
    if (lowerMessage.includes('login') || lowerMessage.includes('security')) 
      return 'border-l-orange-500 bg-orange-50/30';
    return 'border-l-gray-300 bg-gray-50/30';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    return date.toLocaleDateString('en-US', options);
  };

  const handleShowMore = () => {
    setVisibleNotifications((prev) => prev + 10);
  };

  const getNotificationActions = (notification) => {
    const message = notification.message.toLowerCase();
    const actions = [];

    // Order-related notifications
    if (message.includes('order')) {
      actions.push({
        type: 'order_history',
        label: 'Order History',
        onClick: () => navigate('/user/orderhistory')
      });
    }

    // Payment-related notifications
    if (message.includes('payment') || message.includes('paid') || message.includes('transaction')) {
      actions.push({
        type: 'view_receipt',
        label: 'View Orders',
        onClick: () => navigate('/user/orderhistory')
      });
    }

    // Transfer/Wallet notifications
    if (message.includes('transfer') || message.includes('wallet') || message.includes('balance')) {
      actions.push({
        type: 'view_wallet',
        label: 'View Profile',
        onClick: () => navigate('/user/profile')
      });
    }

    // Security/Login notifications
    if (message.includes('login') || message.includes('security') || message.includes('password')) {
      actions.push({
        type: 'security',
        label: 'Account Settings',
        onClick: () => navigate('/user/profile')
      });
    }

    // Delivery notifications
    if (message.includes('delivery') || message.includes('driver') || message.includes('arrived')) {
      actions.push({
        type: 'track_order',
        label: 'Track Order',
        onClick: () => navigate('/user/orderhistory')
      });
    }

    return actions;
  };

  const handleNotificationClick = (notification) => {
    // Show the notification using the enhanced system
    showBackendNotification(notification);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-6 px-4 pt-16 md:pt-24 pb-24 md:pb-6">
      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl md:text-4xl font-bold text-crust mb-2 flex items-center justify-center gap-3 font-secondary">
            <FaBell className="text-pepperoni" />
            Notifications
          </h1>
          <p className="text-gray-600 font-sans">Stay updated with your latest activities</p>
        </motion.div>

        {/* Error State */}
        {error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-red-50 border-l-4 border-pepperoni rounded-lg p-4 mb-6 flex items-center gap-3"
          >
            <FaExclamationTriangle className="text-pepperoni flex-shrink-0" />
            <span className="font-sans text-crust">{error}</span>
          </motion.div>
        )}

        {/* Notifications List */}
        <div className="space-y-3">
          {notifications.length > 0 ? (
            <AnimatePresence>
              {notifications.slice(0, visibleNotifications).map((notification, index) => {
                const actions = getNotificationActions(notification);
                const borderClass = getNotificationBorder(notification.message);
                return (
                  <motion.div
                    key={notification._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className={`bg-white rounded-lg shadow-sm border-l-4 ${borderClass} p-4 hover:shadow-md transition-all duration-200 cursor-pointer group`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5 text-lg">
                        {getNotificationIcon(notification.message)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-crust font-medium leading-relaxed font-sans text-sm mb-1">
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                          <FaClock className="text-xs" />
                          <span className="font-sans">{formatDate(notification.createdAt)}</span>
                        </div>
                        
                        {/* Action Buttons */}
                        {actions.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {actions.map((action, actionIndex) => (
                              <motion.button
                                key={actionIndex}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  action.onClick();
                                }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-crust text-white text-xs font-medium rounded-md hover:bg-gray-800 transition-all duration-200 shadow-sm"
                              >
                                {action.type === 'order_history' && <FaHistory />}
                                {action.type === 'view_receipt' && <FaReceipt />}
                                {action.type === 'view_wallet' && <FaShoppingBag />}
                                {action.type === 'security' && <FaCheckCircle />}
                                {action.type === 'track_order' && <FaEye />}
                                <span>{action.label}</span>
                              </motion.button>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Subtle chevron indicator */}
                      <div className="flex-shrink-0 opacity-30 group-hover:opacity-60 transition-opacity duration-200">
                        <FaChevronRight className="text-crust text-xs" />
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          ) : (
            !loading && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-16"
              >
                <div className="mb-6">
                  <FaBell className="text-6xl text-gray-200 mx-auto mb-4" />
                  <h2 className="text-2xl font-semibold text-crust mb-2 font-secondary">No notifications yet</h2>
                  <p className="text-gray-500 font-sans">You'll see your notifications here when they arrive!</p>
                </div>
              </motion.div>
            )
          )}

          {/* Loading State */}
          {loading && <LoadingPage />}

          {/* Load More Button */}
          {visibleNotifications < notifications.length && !loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-center pt-6"
            >
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleShowMore}
                className="bg-crust text-white px-6 py-3 rounded-lg font-medium shadow-sm hover:shadow-md transition-all duration-300 flex items-center gap-2 font-sans"
              >
                <FaBell />
                Show More ({notifications.length - visibleNotifications} remaining)
              </motion.button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notifications;
